import React, { useState, useEffect, useRef } from "react";
import {
  X, ChevronLeft, ChevronRight, Check, Loader2,
  Trophy, Play, MapPin, Dumbbell, Plus, Minus, Star, Zap,
} from "lucide-react";
import { getUserToken } from "../context/UserAuthContext";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

function getYouTubeEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}?rel=0&playsinline=1` : null;
}
function fotoSrc(url) {
  if (!url) return null;
  if (url.startsWith("/api/")) return `${API}${url}`;
  return url;
}
function exKey(ej) {
  return ej.nombre || ej.nombre_maquina || ej.exercise_id || "";
}

export default function GuidedWorkoutMode({ day, exercises, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [exIdx, setExIdx] = useState(0);
  const [finished, setFinished] = useState(false);
  const [saving, setSaving] = useState(false);
  const [videoModal, setVideoModal] = useState(false);
  const contentRef = useRef(null);

  // workoutData: { [key]: { series: [{reps, peso_kg}], savedThisSession: bool } }
  const [workoutData, setWorkoutData] = useState(() => {
    const init = {};
    for (const ej of exercises) {
      const key = exKey(ej);
      const numSeries = ej.series || 3;
      init[key] = {
        series: Array.from({ length: numSeries }, () => ({ reps: "", peso_kg: "" })),
        savedThisSession: false,
      };
    }
    return init;
  });

  const [bestRecords, setBestRecords] = useState({});
  const [todayRecords, setTodayRecords] = useState({});  // pre-loaded today's saved data

  // Lock body scroll — iOS-safe approach (prevents momentum scroll on Safari)
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Ensure content starts at top on mount
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, []);

  // Fetch best records + today's records for all exercises
  useEffect(() => {
    const token = getUserToken();
    Promise.all(
      exercises.map(async (ej) => {
        const key = exKey(ej);
        const enc = encodeURIComponent(key);
        try {
          const [bestRes, todayRes] = await Promise.all([
            fetch(`${API}/api/evolution/mejor-registro/${enc}`, { headers: { Authorization: `Bearer ${token}` } }),
            fetch(`${API}/api/evolution/registro-hoy/${enc}`, { headers: { Authorization: `Bearer ${token}` } }),
          ]);
          const best = bestRes.ok ? await bestRes.json() : null;
          const todayRec = todayRes.ok ? await todayRes.json() : null;
          return [key, { best, todayRec }];
        } catch { return [key, { best: null, todayRec: null }]; }
      })
    ).then((entries) => {
      const bestMap = {};
      const todayMap = {};
      entries.forEach(([key, { best, todayRec }]) => {
        bestMap[key] = best;
        if (todayRec?.series?.length > 0) todayMap[key] = todayRec;
      });
      setBestRecords(bestMap);
      setTodayRecords(todayMap);
      // Pre-fill workoutData with today's saved series
      if (Object.keys(todayMap).length > 0) {
        setWorkoutData((prev) => {
          const updated = { ...prev };
          Object.entries(todayMap).forEach(([key, rec]) => {
            updated[key] = {
              series: rec.series.map((s) => ({ reps: String(s.reps), peso_kg: String(s.peso_kg) })),
              savedThisSession: true, // already saved today — only re-save if user edits
            };
          });
          return updated;
        });
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToTop = () => { if (contentRef.current) contentRef.current.scrollTop = 0; };

  const saveExercise = async (idx) => {
    const ej = exercises[idx];
    if (!ej) return;
    const key = exKey(ej);
    const data = workoutData[key];
    if (!data || data.savedThisSession) return;
    const validSeries = data.series.filter((s) => s.reps !== "" && s.peso_kg !== "");
    if (!validSeries.length) return;
    try {
      await fetch(`${API}/api/evolution/entreno`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getUserToken()}` },
        body: JSON.stringify({
          ejercicio_nombre: key,
          numero_maquina: ej.numero_maquina || null,
          series: validSeries.map((s) => ({ reps: parseInt(s.reps), peso_kg: parseFloat(s.peso_kg) })),
          fecha: today,
        }),
      });
      setWorkoutData((prev) => ({ ...prev, [key]: { ...prev[key], savedThisSession: true } }));
    } catch { /* silent — data preserved in state */ }
  };

  const handleNext = async () => {
    setSaving(true);
    await saveExercise(exIdx);
    setSaving(false);
    if (exIdx >= exercises.length - 1) {
      // Mark day as completed regardless of whether reps were logged
      try {
        await fetch(`${API}/api/evolution/completar-sesion`, {
          method: "POST",
          headers: { Authorization: `Bearer ${getUserToken()}` },
        });
      } catch { /* silent */ }
      setFinished(true);
    } else {
      setExIdx((i) => i + 1);
      setVideoModal(false);
      scrollToTop();
    }
  };

  const handlePrev = async () => {
    if (exIdx === 0) return;
    setSaving(true);
    await saveExercise(exIdx);
    setSaving(false);
    setExIdx((i) => i - 1);
    setVideoModal(false);
    scrollToTop();
  };

  const handleClose = async () => {
    await saveExercise(exIdx);
    onClose();
  };

  const updateSeries = (serieIdx, field, val) => {
    const key = exKey(exercises[exIdx]);
    setWorkoutData((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        savedThisSession: false,  // mark dirty so saveExercise will re-save
        series: prev[key].series.map((s, i) => (i === serieIdx ? { ...s, [field]: val } : s)),
      },
    }));
  };

  const addSerie = () => {
    const key = exKey(exercises[exIdx]);
    setWorkoutData((prev) => ({
      ...prev,
      [key]: { ...prev[key], savedThisSession: false, series: [...prev[key].series, { reps: "", peso_kg: "" }] },
    }));
  };

  const removeSerie = (serieIdx) => {
    const key = exKey(exercises[exIdx]);
    if (workoutData[key].series.length <= 1) return;
    setWorkoutData((prev) => ({
      ...prev,
      [key]: { ...prev[key], savedThisSession: false, series: prev[key].series.filter((_, i) => i !== serieIdx) },
    }));
  };

  // ── Completion screen ──────────────────────────────────────────────────────
  if (finished) {
    const summary = exercises.map((ej) => {
      const key = exKey(ej);
      const data = workoutData[key];
      const validSeries = data?.series.filter((s) => s.reps !== "" && s.peso_kg !== "") || [];
      return { nombre: key, numSeries: validSeries.length };
    });
    const done = summary.filter((s) => s.numSeries > 0).length;

    return (
      <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center p-6" data-testid="workout-complete-screen">
        <div className="max-w-sm w-full">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-[#F2C230] flex items-center justify-center">
              <Trophy size={40} className="text-black" />
            </div>
          </div>
          <h1 className="text-3xl font-black text-center mb-1">¡Día completado!</h1>
          <p className="text-white/40 text-center text-sm mb-8">
            Has terminado el plan del Día {day} · {done}/{exercises.length} ejercicios registrados
          </p>

          <div className="border border-white/10 divide-y divide-white/8 mb-8">
            {summary.map((s, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <div className={`w-5 h-5 flex items-center justify-center shrink-0 ${s.numSeries > 0 ? "bg-green-500/20 text-green-400" : "bg-white/6 text-white/20"}`}>
                    <Check size={11} strokeWidth={3} />
                  </div>
                  <span className="text-sm text-white/80">{s.nombre}</span>
                </div>
                <span className="text-xs text-white/30">
                  {s.numSeries > 0 ? `${s.numSeries} serie${s.numSeries > 1 ? "s" : ""}` : "Sin registrar"}
                </span>
              </div>
            ))}
          </div>

          <button onClick={onClose} data-testid="guided-finish-btn"
            className="w-full py-4 bg-[#F2C230] text-black font-black text-sm tracking-[0.2em] uppercase hover:bg-white transition-colors">
            Volver a mi rutina
          </button>
        </div>
      </div>
    );
  }

  // ── Exercise screen ────────────────────────────────────────────────────────
  const ej = exercises[exIdx];
  if (!ej) return null;

  const key = exKey(ej);
  const currentData = workoutData[key] || { series: [], savedThisSession: false };
  const bestRecord = bestRecords[key] || null;
  const src = fotoSrc(ej.foto_url);
  const embedUrl = getYouTubeEmbed(ej.video_url);
  const hasMachine = ej.numero_maquina != null;
  const grupos = Array.isArray(ej.grupo_muscular)
    ? ej.grupo_muscular.join(" · ")
    : ej.grupo_muscular || null;
  const isLast = exIdx === exercises.length - 1;

  return (
    <div className="fixed inset-0 z-50 bg-[#070707] flex flex-col" data-testid="guided-workout-mode">

      {/* ── Header ── */}
      <div className="shrink-0 px-4 pt-safe-top pt-4 pb-3 bg-[#070707] border-b border-white/8">
        <div className="flex items-center justify-between mb-3">
          <button onClick={handleClose} data-testid="guided-close-btn"
            className="w-9 h-9 flex items-center justify-center border border-white/12 text-white/35 hover:text-white hover:border-white/25 transition-colors">
            <X size={16} />
          </button>
          <div className="text-center">
            <p className="text-[10px] font-black tracking-[0.25em] uppercase text-[#F2C230]">Día {day}</p>
            <p className="text-[10px] text-white/35 mt-0.5">Ejercicio {exIdx + 1} de {exercises.length}</p>
          </div>
          <div className="w-9" />
        </div>
        <div className="h-1 bg-white/8 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#F2C230] rounded-full transition-all duration-400"
            style={{ width: `${((exIdx + 1) / exercises.length) * 100}%` }}
            data-testid="guided-progress-bar"
          />
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div ref={contentRef} className="flex-1 overflow-y-auto overscroll-contain pb-24">

        {/* Photo — tap to play video */}
        {src ? (
          <div className="relative w-full aspect-video overflow-hidden bg-black shrink-0">
            <img src={src} alt={key} className="w-full h-full object-cover" />
            {(embedUrl || ej.video_url) && (
              <button
                onClick={() => setVideoModal(true)}
                data-testid="guided-video-play-btn"
                className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors group"
              >
                <div className="w-14 h-14 rounded-full bg-black/70 border border-white/30 flex items-center justify-center group-hover:bg-[#F2C230] group-hover:border-[#F2C230] transition-colors">
                  <Play size={22} fill="white" className="group-hover:fill-black ml-1" />
                </div>
                <span className="absolute bottom-3 left-0 right-0 text-center text-[11px] font-black tracking-[0.2em] uppercase text-white/70">Ver tutorial</span>
              </button>
            )}
          </div>
        ) : (ej.video_url) && (
          /* No photo but has video — show a plain play button row */
          <button
            onClick={() => setVideoModal(true)}
            data-testid="guided-video-play-btn"
            className="flex items-center justify-center gap-2 border border-white/10 text-white/40 py-4 text-xs font-bold tracking-wider uppercase hover:border-[#F2C230] hover:text-[#F2C230] transition-colors"
          >
            <Play size={13} fill="currentColor" /> Ver vídeo tutorial
          </button>
        )}

        <div className="p-5 space-y-5">

          {/* Exercise identity */}
          <div className="flex items-start gap-3">
            {hasMachine ? (
              <Link to={`/maquina/${ej.numero_maquina}`} onClick={handleClose}
                className="shrink-0 flex flex-col items-center justify-center w-13 h-13 min-w-[52px] min-h-[52px] bg-[#F2C230] text-black hover:bg-white transition-colors">
                <span className="font-black text-[9px] leading-none">MÁQ</span>
                <span className="font-black text-lg leading-none">{ej.numero_maquina}</span>
              </Link>
            ) : (
              <div className="shrink-0 w-[52px] h-[52px] flex items-center justify-center border border-white/12 bg-white/4">
                <Dumbbell size={18} className="text-white/35" />
              </div>
            )}
            <div className="min-w-0">
              <h2 className="text-xl font-black leading-tight" data-testid="guided-exercise-name">{key}</h2>
              {grupos && (
                <div className="flex items-center gap-1 mt-1">
                  <Zap size={10} className="text-[#F2C230] shrink-0" />
                  <span className="text-xs text-[#F2C230] font-bold">{grupos}</span>
                </div>
              )}
              {ej.planta && (
                <div className="flex items-center gap-1 mt-0.5">
                  <MapPin size={10} className="text-white/35 shrink-0" />
                  <span className="text-xs text-white/45">{ej.planta}</span>
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          {ej.instrucciones_breves && (
            <div className="p-4 border border-white/8 bg-white/[0.03]">
              <p className="text-[10px] font-black tracking-[0.2em] uppercase text-[#F2C230] mb-2">Cómo hacerlo</p>
              <p className="text-sm text-white/75 leading-relaxed">{ej.instrucciones_breves}</p>
            </div>
          )}

          {/* Best record */}
          {bestRecord && (
            <div className="p-3 bg-[#F2C230]/6 border border-[#F2C230]/18" data-testid="guided-best-record">
              <div className="flex items-center gap-1.5 mb-2">
                <Star size={11} className="text-[#F2C230]" fill="currentColor" />
                <p className="text-[10px] font-black tracking-[0.2em] uppercase text-[#F2C230]">Mejor registro</p>
                {bestRecord.fecha && (
                  <span className="ml-auto text-[10px] text-white/25">
                    {new Date(bestRecord.fecha + "T12:00:00").toLocaleDateString("es-ES", { day: "numeric", month: "short" })}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {bestRecord.series?.map((s, i) => (
                  <span key={i} className="text-xs font-bold text-[#F2C230]/90 bg-[#F2C230]/10 px-2.5 py-1.5 border border-[#F2C230]/15">
                    {s.reps} rep × {s.peso_kg} kg
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Series input */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[10px] font-black tracking-[0.2em] uppercase text-white/50">Registrar series</p>
                <p className="text-[10px] text-white/30 mt-0.5">Objetivo: {ej.series} × {ej.repeticiones} reps</p>
              </div>
              {currentData.savedThisSession && (
                <span className="flex items-center gap-1 text-[10px] text-green-400 font-black tracking-wider">
                  <Check size={10} strokeWidth={3} /> Guardado
                </span>
              )}
            </div>

            <div className="grid grid-cols-[20px_1fr_1fr_32px] gap-x-2 gap-y-2 items-center mb-1">
              <div />
              <p className="text-[10px] font-black uppercase tracking-widest text-white/35 text-center">Reps</p>
              <p className="text-[10px] font-black uppercase tracking-widest text-white/35 text-center">Kg</p>
              <div />
            </div>

            <div className="space-y-2">
              {currentData.series.map((s, i) => (
                <div key={i} className="grid grid-cols-[20px_1fr_1fr_32px] gap-x-2 items-center">
                  <span className="text-[11px] font-black text-white/25 text-right">{i + 1}</span>
                  <input
                    type="number" inputMode="numeric" min="1"
                    value={s.reps}
                    onChange={(e) => updateSeries(i, "reps", e.target.value)}
                    placeholder={String(ej.repeticiones || "—")}
                    data-testid={`guided-reps-${i}`}
                    className="w-full bg-[#1a1a1a] border border-white/18 text-white text-center py-3.5 text-base font-black outline-none focus:border-[#F2C230] focus:bg-[#1e1e1e] transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none placeholder:text-white/20"
                  />
                  <input
                    type="number" inputMode="decimal" min="0" step="0.5"
                    value={s.peso_kg}
                    onChange={(e) => updateSeries(i, "peso_kg", e.target.value)}
                    placeholder="—"
                    data-testid={`guided-peso-${i}`}
                    className="w-full bg-[#1a1a1a] border border-white/18 text-white text-center py-3.5 text-base font-black outline-none focus:border-[#F2C230] focus:bg-[#1e1e1e] transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none placeholder:text-white/20"
                  />
                  <button onClick={() => removeSerie(i)} disabled={currentData.series.length <= 1}
                    className="w-8 h-8 flex items-center justify-center text-white/18 hover:text-red-400 transition-colors disabled:opacity-20">
                    <Minus size={14} />
                  </button>
                </div>
              ))}
            </div>

            <button onClick={addSerie} data-testid="guided-add-serie"
              className="mt-3 flex items-center gap-1.5 text-[11px] font-black tracking-wider uppercase text-white/30 hover:text-[#F2C230] transition-colors">
              <Plus size={12} /> Añadir serie
            </button>
          </div>

        </div>
      </div>

      {/* ── Video modal ── */}
      {videoModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4" data-testid="video-modal">
          <button onClick={() => setVideoModal(false)}
            className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center border border-white/20 text-white/60 hover:text-white hover:border-white/40 transition-colors bg-black/70 backdrop-blur-sm"
            data-testid="video-modal-close">
            <X size={18} />
          </button>
          {embedUrl ? (
            <div className="w-full max-w-sm mx-auto">
              <div className="relative aspect-[9/16] overflow-hidden rounded-xl bg-black ring-1 ring-white/15">
                <iframe
                  src={embedUrl + "&autoplay=1"}
                  title="Tutorial"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            </div>
          ) : (
            <div className="w-full max-w-lg mx-auto aspect-video bg-black rounded overflow-hidden ring-1 ring-white/15">
              <iframe src={ej.video_url} title="Tutorial" allowFullScreen className="w-full h-full" />
            </div>
          )}
        </div>
      )}

      {/* ── Footer navigation ── */}
      <div className="shrink-0 border-t border-white/8 px-4 py-4 bg-[#070707] flex gap-3">
        <button
          onClick={handlePrev}
          disabled={exIdx === 0 || saving}
          data-testid="guided-prev-btn"
          className="w-12 h-12 flex items-center justify-center border border-white/10 text-white/35 hover:text-white hover:border-white/25 transition-colors disabled:opacity-20 shrink-0"
        >
          <ChevronLeft size={20} />
        </button>

        <button
          onClick={handleNext}
          disabled={saving}
          data-testid="guided-next-btn"
          className="flex-1 h-12 flex items-center justify-center gap-2 bg-[#F2C230] text-black font-black text-sm tracking-[0.15em] uppercase hover:bg-white transition-colors disabled:opacity-50"
        >
          {saving
            ? <Loader2 size={16} className="animate-spin" />
            : isLast
            ? <><Check size={16} strokeWidth={3} /> Finalizar día</>
            : <>Siguiente <ChevronRight size={16} /></>
          }
        </button>
      </div>

    </div>
  );
}
